import { useEffect, useState } from 'react';
import { DeviceTokens, type DeviceTokenRow } from '../lib/api';
import { confirmDialog } from '../components/ui/confirm';

export function SettingsPage() {
  const [tokens, setTokens] = useState<DeviceTokenRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await DeviceTokens.list();
      setTokens(r.tokens);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function generate() {
    setCreating(true);
    setError(null);
    try {
      const r = await DeviceTokens.create(newDescription.trim() || undefined);
      setNewToken(r.token);
      setNewDescription('');
      setCopied(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create token');
    } finally {
      setCreating(false);
    }
  }

  async function revoke(t: DeviceTokenRow) {
    const ok = await confirmDialog({
      title: 'Revoke token?',
      body: `"${t.description ?? 'unnamed token'}" — any device using this token will stop receiving notifications.`,
      tone: 'danger',
      confirmLabel: 'Revoke',
    });
    if (!ok) return;
    try {
      await DeviceTokens.revoke(t.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Revoke failed');
    }
  }

  async function copyToken() {
    if (!newToken) return;
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
    } catch {
      /* ignore */
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Device tokens</h2>
        <p className="text-sm text-slate-500">Manage device tokens used by the desktop notifier app.</p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <section className="space-y-3">
        <p className="text-xs text-slate-500">
          The J1L Ticketing desktop notifier (Windows tray app) needs a token to authenticate
          your computer. Generate one, paste it into the app on first run, and you're set.
          Tokens are long-lived; revoke any that you no longer use.
        </p>

        {newToken && (
          <div className="bg-amber-50 border border-amber-300 rounded-md p-3 space-y-2">
            <div className="text-sm font-medium text-amber-900">
              Copy this token now — you won't see it again.
            </div>
            <div className="flex gap-2">
              <code className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5 text-xs font-mono break-all">
                {newToken}
              </code>
              <button
                onClick={copyToken}
                className="px-3 py-1.5 rounded-md bg-amber-600 text-white text-sm hover:bg-amber-700 whitespace-nowrap"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            <button
              onClick={() => setNewToken(null)}
              className="text-xs text-amber-800 hover:text-amber-900"
            >
              I've saved it — dismiss
            </button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md p-3 flex gap-2">
          <input
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Description (e.g. 'Office desktop')"
            maxLength={200}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm"
          />
          <button
            onClick={() => void generate()}
            disabled={creating}
            className="px-3 py-2 rounded-md bg-brand-600 text-white text-sm hover:bg-brand-700 disabled:opacity-60 whitespace-nowrap"
          >
            {creating ? 'Generating…' : 'Generate token'}
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-slate-500">No tokens yet.</p>
        ) : (
          <ul className="space-y-2">
            {tokens.map((t) => (
              <li key={t.id} className="bg-white border border-slate-200 rounded-md p-3 flex gap-3 items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 text-sm">
                    {t.description ?? <span className="text-slate-500 italic">(no description)</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Created {new Date(t.createdAt).toLocaleString()}
                    {' · '}
                    {t.lastUsedAt
                      ? `Last used ${new Date(t.lastUsedAt).toLocaleString()}`
                      : 'Never used'}
                  </div>
                </div>
                <button
                  onClick={() => void revoke(t)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
